import React, { useCallback, useRef, useState } from "react";

import type { IAfterGuiAttachedParams } from "ag-grid-community";
import type { CustomFilterDisplayProps } from "ag-grid-react";
import { useGridFilterDisplay } from "ag-grid-react";
import { DateRangePickerModal } from "components";
import { Checkbox, Space, Tooltip } from "antd";
import { DateUtils } from "utils/dateutils";
import { BLANKS_FILTER_KEY, LEGACY_BLANK_DATE_KEYS } from "constants/index";

import "./custom-date-filter.css";

const PRESET_FILTERS = ["Past today", "Today", "Next 3 days", "Next 2 weeks"];

/**
 * Display order for the checkbox list, and the whitelist handleFilterToggle
 * re-sorts against. BLANKS_FILTER_KEY has to be in here or it is silently
 * dropped on the next toggle -- the bug that made the handler's blank-date
 * support unreachable from the UI.
 */
const TOGGLEABLE_FILTERS = [...PRESET_FILTERS, BLANKS_FILTER_KEY];

/**
 * Dashboard drill-downs and views saved before CIQ-12252 carry their own blank
 * sentinels; show them as the checkbox the user recognises.
 */
const normalizeValues = (values: string[]) =>
  values.map((value) =>
    LEGACY_BLANK_DATE_KEYS.includes(value) ? BLANKS_FILTER_KEY : value
  );

export function CustomDateFilterComponent({
  model,
  onModelChange,
  colDef,
  isCustomFilterOnly,
  presets,
  maxRangeDays,
  allowCustomWithPresets,
  includeBlanks = true
}: CustomFilterDisplayProps & {
  isCustomFilterOnly?: boolean;
  /** Overrides the due-date presets for grids that look backwards in time. */
  presets?: string[];
  /** Caps how far apart the two Custom dates may be. */
  maxRangeDays?: number;
  /**
   * Lets Custom be picked while a preset is ticked, replacing it. Grids that
   * seed a preset need this, or Custom is permanently greyed out.
   */
  allowCustomWithPresets?: boolean;
  /** Drops the "(Blanks)" option for columns that are never empty. */
  includeBlanks?: boolean;
}) {
  const refInput = useRef<HTMLInputElement>(null);
  const [customDateRange, setCustomDateRange] = useState<any>(null);
  const [isCustomDatePickerOpen, setIsCustomDatePickerOpen] =
    useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>(() => {
    if (!model) return [];
    if (model.filterType === "date" && model.type === "inRange")
      return ["Custom"];
    // Programmatic models (dashboard drill-downs) omit filterType entirely, so
    // key off the values array rather than the type.
    if (Array.isArray(model.values)) return normalizeValues(model.values);
    return [];
  });
  const toggleableFilters = presets?.length
    ? [...presets, ...(includeBlanks ? [BLANKS_FILTER_KEY] : [])]
    : TOGGLEABLE_FILTERS;
  const dueDateFilters = isCustomFilterOnly
    ? [BLANKS_FILTER_KEY, "Custom"]
    : toggleableFilters;

  const afterGuiAttached = useCallback((params?: IAfterGuiAttachedParams) => {
    if (!params?.suppressFocus) {
      window.setTimeout(() => {
        refInput.current?.focus();
      });
    }
  }, []);

  useGridFilterDisplay({
    afterGuiAttached
  });

  const onCustomDateFilterChanged = () => {
    const from = customDateRange?.[0];
    const to = customDateRange?.[1];
    if (from && to) {
      setSelectedFilters(["Custom"]);
      onModelChange({
        values: ["CustomDateRange"],
        filterType: "date",
        type: "inRange",
        dateFrom: DateUtils.format(from),
        dateTo: DateUtils.format(to)
      });
    } else if (!selectedFilters.length) {
      // Dismissing the picker without a range must not install a model that
      // matches nothing, and must not wipe an existing preset/(Blanks) choice.
      onModelChange(null);
    }
  };

  const handleFilterToggle = (item: string) => {
    if (item === "Custom") {
      setIsCustomDatePickerOpen(true);
      return;
    }

    const isSelected = selectedFilters.includes(item);
    let updatedFilters: string[];

    if (isSelected) {
      updatedFilters = selectedFilters.filter((f) => f !== item);
    } else {
      // Remove "Custom" if a preset is being selected
      updatedFilters = [...selectedFilters.filter((f) => f !== "Custom"), item];
    }

    // Maintain consistent order based on the offered list to ensure ag-grid
    // detects model changes. Anything not in this list is discarded here, which
    // is why BLANKS_FILTER_KEY must be part of it.
    updatedFilters = toggleableFilters.filter((f) =>
      updatedFilters.includes(f)
    );

    setSelectedFilters(updatedFilters);

    if (updatedFilters.length === 0) {
      onModelChange(null);
    } else {
      onModelChange({ values: [...updatedFilters], filterType: "set" });
    }
  };

  const hasPresetSelected = selectedFilters.some((f) =>
    toggleableFilters.includes(f)
  );
  const hasCustomSelected = selectedFilters.includes("Custom");
  const customLocked = hasPresetSelected && !allowCustomWithPresets;

  return (
    <>
      <div className="custom-date-filters">
        <div className="filter-list">
          {dueDateFilters.map((item) => {
            const isCustom = item === "Custom";
            const isCustomDisabled =
              isCustom && hasPresetSelected && !allowCustomWithPresets;
            const isPresetDisabled = !isCustom && hasCustomSelected;
            const isDisabled = isCustomDisabled || isPresetDisabled;
            return (
              <Tooltip
                key={item}
                title={
                  isPresetDisabled
                    ? "Remove Custom to select predefined options"
                    : ""
                }
              >
                <Space
                  className={`filter-list-item${isDisabled ? " filter-item-disabled" : ""}`}
                  onClick={() => !isDisabled && handleFilterToggle(item)}
                >
                  {!isCustom && (
                    <Checkbox
                      checked={selectedFilters.includes(item)}
                      disabled={isPresetDisabled}
                      style={{ marginRight: 8 }}
                    />
                  )}
                  {item}
                </Space>
              </Tooltip>
            );
          })}
        </div>
        {!isCustomFilterOnly && (
          <>
            <div className="custom-option-divider" />
            <Tooltip
              title={
                hasPresetSelected && !allowCustomWithPresets
                  ? "Deselect predefined options to select Custom"
                  : ""
              }
            >
              <span className="custom-option-wrapper">
                <button
                  type="button"
                  tabIndex={customLocked ? -1 : 0}
                  className={`custom-option${customLocked ? " disabled" : ""}${selectedFilters.includes("Custom") ? " selected" : ""}`}
                  onClick={() => !customLocked && handleFilterToggle("Custom")}
                >
                  Custom
                </button>
              </span>
            </Tooltip>
          </>
        )}
      </div>
      <DateRangePickerModal
        title={colDef.headerName}
        isOpen={isCustomDatePickerOpen}
        setIsOpen={setIsCustomDatePickerOpen}
        customDateRange={customDateRange}
        setCustomDateRange={setCustomDateRange}
        onOkClick={onCustomDateFilterChanged}
        maxRangeDays={maxRangeDays}
        hasActiveFilter={hasCustomSelected}
        onClear={() => {
          setSelectedFilters([]);
          onModelChange(null);
        }}
      />
    </>
  );
}
